import type { CSSProperties, ReactNode } from 'react'
import { radius } from '@stagepost/web/components/primitives/shape'

/**
 * The pieces the marketing page repeats.
 *
 * Same reasoning as `primitives/` — the design file spells out the same eyebrow
 * eight times and the same card thirty, and every one of those is a chance for
 * a value to drift. These are the definitions. They are marketing-only on
 * purpose: the app's surfaces are `Panel`, and a landing page card is a
 * different thing with a different job.
 */

/** The small uppercase label that names a section before its heading does. */
export function Eyebrow({ children, accent = false }: { children: ReactNode; accent?: boolean }) {
  return (
    <div
      style={{
        font: '500 11px var(--font-machine)',
        letterSpacing: '.2em',
        textTransform: 'uppercase',
        color: accent ? 'var(--accent)' : 'var(--text-dim)',
        marginBottom: 18,
      }}
    >
      {children}
    </div>
  )
}

/** The same label at card scale, where it introduces a panel rather than a section. */
export function CardLabel({ children, accent = false }: { children: ReactNode; accent?: boolean }) {
  return (
    <div
      style={{
        font: '500 10.5px var(--font-machine)',
        letterSpacing: '.14em',
        textTransform: 'uppercase',
        color: accent ? 'var(--accent)' : 'var(--text-dim)',
        marginBottom: 14,
      }}
    >
      {children}
    </div>
  )
}

/**
 * A section of the page.
 *
 * The hairline above is the only separator the design uses; there are no boxes
 * around sections and no alternating backgrounds.
 */
export function Section({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <section
      id={id}
      className="sp-page-section"
      style={{ paddingBottom: 'clamp(56px,8vw,100px)' }}
    >
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 'clamp(40px,6vw,72px)' }}>{children}</div>
    </section>
  )
}

/** Eyebrow, heading and lead paragraph, which every section opens with. */
export function SectionHead({
  eyebrow,
  title,
  lead,
  titleWidth = 760,
  gap = 44,
}: {
  eyebrow: string
  title: ReactNode
  lead?: ReactNode
  titleWidth?: number
  gap?: number
}) {
  return (
    <>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2
        style={{
          margin: `0 0 ${lead ? 20 : gap}px`,
          fontSize: 'clamp(26px,3.2vw,40px)',
          lineHeight: 1.12,
          fontWeight: 800,
          letterSpacing: '-.035em',
          maxWidth: titleWidth,
          textWrap: 'balance',
        }}
      >
        {title}
      </h2>
      {lead && (
        <p
          style={{
            margin: `0 0 ${gap}px`,
            fontSize: 'clamp(15px,1.4vw,17.5px)',
            lineHeight: 1.65,
            color: 'var(--text-body)',
            maxWidth: 700,
            textWrap: 'pretty',
          }}
        >
          {lead}
        </p>
      )}
    </>
  )
}

/**
 * A card. `accent` marks the one card in a group that is the point of the
 * group — there is never more than one, or it stops meaning anything.
 */
export function Card({
  children,
  accent = false,
  style,
}: {
  children: ReactNode
  accent?: boolean
  style?: CSSProperties
}) {
  return (
    <div
      style={{
        background: accent ? 'rgba(110,168,254,.07)' : 'var(--panel)',
        border: `1px solid ${accent ? 'var(--accent-edge)' : 'var(--border)'}`,
        borderRadius: radius.lg,
        padding: 24,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/** A card's own heading. */
export function CardTitle({ children, size = 16.5 }: { children: ReactNode; size?: number }) {
  return (
    <div style={{ fontSize: size, fontWeight: 700, letterSpacing: '-.02em', marginBottom: 10 }}>{children}</div>
  )
}

/** A card's body copy. */
export function CardBody({ children, bright = false }: { children: ReactNode; bright?: boolean }) {
  return (
    <div
      style={{
        fontSize: 13.5,
        lineHeight: 1.6,
        color: bright ? 'var(--text-body)' : 'var(--text-dim)',
        textWrap: 'pretty',
      }}
    >
      {children}
    </div>
  )
}

/** A bulleted line. The dot carries the meaning, so it is the only coloured part. */
export function Bullet({ children, color = 'var(--additions)' }: { children: ReactNode; color?: string }) {
  return (
    <li style={{ display: 'flex', gap: 11 }}>
      <span
        aria-hidden="true"
        style={{ width: 5, height: 5, borderRadius: '50%', background: color, marginTop: 7, flex: 'none' }}
      />
      <span style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-body)' }}>{children}</span>
    </li>
  )
}

/** A grid that fits as many cards per row as the viewport allows. */
export function CardGrid({ children, minimum = 248 }: { children: ReactNode; minimum?: number }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(${minimum}px,1fr))`, gap: 18 }}>
      {children}
    </div>
  )
}

/** The action beside the primary one. Never two accents in a row. */
export function SecondaryAction({
  children,
  href,
  onClick,
  compact = false,
}: {
  children: ReactNode
  href: string
  onClick?: () => void
  compact?: boolean
}) {
  return (
    <a
      href={href}
      onClick={onClick}
      style={{
        display: 'inline-block',
        padding: compact ? '11px 18px' : '15px 26px',
        borderRadius: compact ? radius.md : radius.action,
        background: 'var(--panel-strong)',
        border: '1px solid var(--border-strong)',
        color: 'var(--text)',
        fontSize: compact ? 13.5 : 15.5,
        fontWeight: 600,
      }}
    >
      {children}
    </a>
  )
}
